import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, Flag, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FILTERS = [["pending", "Pending"], ["flagged", "Reported"], ["approved", "Approved"], ["rejected", "Rejected"], ["removed", "Removed"]];
const CAT = { funny: "Funny", flirty: "Flirty", naughty: "Naughty", after_dark: "After Dark 18+" };

function Row({ c, onDone }) {
  const [text, setText] = useState(c.text);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/dare/library/${c.id}/decide`, { decision, text: text !== c.text ? text : undefined, note: note || undefined });
      toast.success(decision === "approved" ? "Approved — now visible to every host" : decision === "rejected" ? "Rejected" : "Removed");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2" data-testid={`dare-admin-item-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400">
        <span className={`uppercase tracking-wider ${c.type === "dare" ? "text-orange-300" : "text-sky-300"}`}>{c.type}</span>
        <span>· {CAT[c.category] || c.category}</span>
        <span>· @{c.author_handle || c.author_id}</span>
        <span className="ml-auto">{c.status}{c.flagged ? " · reported" : ""}</span>
        {c.report_count > 0 && <span className="flex items-center gap-1 text-rose-300"><Flag size={11} /> {c.report_count}</span>}
      </div>
      <Input value={text} onChange={(e) => setText(e.target.value.slice(0, 240))} className="text-sm bg-black/40 border-white/10" data-testid={`dare-admin-text-${c.id}`} />
      {c.moderation?.reason && <div className="text-[11px] text-rose-300">Auto-moderation: {c.moderation.reason}</div>}
      {(c.reports || []).slice(0, 3).map((r, i) => <div key={i} className="text-[11px] text-zinc-400">Report: “{r.reason}”</div>)}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to the author (optional)" className="h-8 text-xs bg-black/40 border-white/10 max-w-xs" data-testid={`dare-admin-note-${c.id}`} />
        <Button size="sm" disabled={busy} className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => decide("approved")} data-testid={`dare-admin-approve-${c.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />} Approve</Button>
        <Button size="sm" variant="outline" disabled={busy} className="h-8 rounded-full border-amber-500/40 text-amber-200" onClick={() => decide("rejected")} data-testid={`dare-admin-reject-${c.id}`}><XCircle size={14} className="mr-1" /> Reject</Button>
        <Button size="sm" variant="outline" disabled={busy} className="h-8 rounded-full border-rose-500/40 text-rose-200" onClick={() => decide("removed")} data-testid={`dare-admin-remove-${c.id}`}><Trash2 size={14} className="mr-1" /> Remove</Button>
      </div>
    </div>
  );
}

/** Owner/admin review queue for host-submitted Dare or Shot challenges. */
export default function DareLibraryTab() {
  const [filter, setFilter] = useState("pending");
  const [data, setData] = useState({ items: [], counts: {} });
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/admin/dare/library", { params: { status: filter } }); setData(d); } catch { toast.error("Could not load dares"); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="dare-admin-tab">
      <div>
        <h2 className="text-base md:text-lg font-semibold text-white">Dare or Shot · Custom Dares</h2>
        <p className="text-xs text-zinc-400">Host submissions must be approved before every host can use them. Explicit acts, nudity, minors and forced drinking are auto-blocked.</p>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" data-testid={`dare-admin-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full border text-xs ${filter === k ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10 text-zinc-300"}`}>
            {l} <span className="text-zinc-500">{data.counts?.[k] ?? 0}</span>
          </button>
        ))}
      </div>
      {data.items.length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center" data-testid="dare-admin-empty">Nothing in this queue.</div>
        : <div className="space-y-2">{data.items.map((c) => <Row key={c.id} c={c} onDone={load} />)}</div>}
    </div>
  );
}
